import { FOLDERS_TAB_KEY, MIN_CRITERIA_LENGTH } from "@/constants";
import { FilterTypes } from "@/enums";
import { IPanelScope, ISearchResult, ITreeItem } from "@/interfaces";
import { findNodeById } from "@/utils/common";
import { HttpService } from "./http.service";
import { FoldersService } from "./folders.service";
import { FiltersService } from "./filters.service";
import { debounce } from "@/utils";

const abortControllers: Record<string, AbortController> = {};
const searchService = () => {
  const search = (
    arr: ITreeItem[],
    criteria: string,
    matchingIds: string[] = [],
  ): {
    filteredTree: ITreeItem[];
    matchingIds: string[];
    fullResults: boolean;
  } => {
    if (criteria.length < MIN_CRITERIA_LENGTH)
      return { filteredTree: arr, matchingIds, fullResults: false };

    const searchString = criteria.toLowerCase();

    const filteredTree = searchString
      ? arr.reduce((acc, item) => {
          let matches = false;
          const itemCopy = { ...item };

          if (
            item.extra?.filterType === FilterTypes.AUTO_FILTER &&
            itemCopy.name
          ) {
            itemCopy.name = itemCopy.name.replace(/\s\(\d+\)$/, "");
          }

          if (item.children?.length) {
            const result = search(item.children, searchString, matchingIds);
            if (result.filteredTree.length) {
              itemCopy.children = result.filteredTree;
              matches = true;
            }
          }

          const { isFoundInList, isFoundInSubList } =
            getIfCriteriaFoundOnMetaData(item, criteria);

          if (item.name?.toLowerCase().includes(searchString)) {
            matches = true;
          } else {
            if (isFoundInList || isFoundInSubList) {
              matches = true;
            }
          }

          if (matches) {
            acc.push(itemCopy);
            matchingIds.push(itemCopy.id);
          }

          return acc;
        }, [] as ITreeItem[])
      : arr;

    const openNodes: string[] = [];

    for (const id of matchingIds) {
      const node = findNodeById(filteredTree, id);

      if (
        node &&
        SearchService.hasDescendantsMatchingCriteria(node, searchString)
      ) {
        openNodes.push(`${node?.serverObject.id}`);
      }
    }

    return {
      filteredTree,
      matchingIds: openNodes,
      fullResults: false,
    };
  };

  const searchNodeTree = (arr: ITreeItem[], id: string): ITreeItem[] => {
    const nodeTree = id
      ? arr.reduce((acc, item) => {
          let matches = false;
          const itemCopy = { ...item };

          if (item.children?.length) {
            const result = searchNodeTree(item.children, id);
            if (result.length) {
              itemCopy.children = result;
              matches = true;
            }
          }

          if (item.id.includes(id)) {
            matches = true;
          }

          if (matches) {
            acc.push(itemCopy);
          }

          return acc;
        }, [] as ITreeItem[])
      : arr;

    return nodeTree;
  };

  const getParentNodeIds = (arr: ITreeItem[], id: string): string[] => {
    const result = searchNodeTree(arr, id);

    const ids: string[] = [];

    const traverse = (result: ITreeItem[]) => {
      for (const item of result) {
        ids.push(item.id);
        if (item.children?.length) {
          traverse(item.children);
        }
      }
    };

    traverse(result);
    return ids;
  };

  const getNumberOfResults = (node: ITreeItem[], criteria: string): number => {
    const searchString = criteria.toLowerCase();
    const countMatchingFilters = (node: ITreeItem): number => {
      let count = 0;

      if (node.name.toLowerCase().includes(searchString)) {
        count++;
      }

      if (node.children) {
        for (const child of node.children) {
          count += countMatchingFilters(child);
        }
      }
      return count;
    };

    let totalMatchingCount = 0;
    for (const rootNode of node) {
      totalMatchingCount += countMatchingFilters(rootNode);
    }
    return totalMatchingCount;
  };

  const findFilterItem = (node: ITreeItem[], id: string): ITreeItem | null => {
    for (const _node of node) {
      if (_node.id == id) {
        return _node;
      }

      if (_node.children) {
        const result = findFilterItem(_node.children, id);
        if (result) {
          return result;
        }
      }
    }

    return null;
  };

  const getParents = (items: ITreeItem[], id: string | null): string[] => {
    if (!items || !id) {
      return [];
    }

    // Helper function to recursively search for parents
    const searchParents = (
      node: ITreeItem,
      targetId: string,
      path: string[],
    ): string[] | null => {
      // If the current node matches the target ID, return the path
      if (node.id === targetId) {
        return path;
      }

      // Iterate through each child and recursively search
      for (const child of node.children) {
        const result = searchParents(child, targetId, [...path, node.id]);
        if (result) {
          return result;
        }
      }

      // If no match is found, return null
      return null;
    };

    // Iterate over each root item in the list and initiate the search
    for (const item of items) {
      const result = searchParents(item, id, []);
      if (result) {
        return result;
      }
    }

    // If no result is found in any of the items, return an empty array
    return [];
  };

  const getIfCriteriaFoundOnMetaData = (
    filter: ITreeItem,
    criteria: string,
  ): { isFoundInList: boolean; isFoundInSubList: boolean } => {
    const isFoundInList = !!filter?.extra?.autoFilterMetaData?.list?.some((s) =>
      s.includes(criteria),
    );
    const isFoundInSubList = !!filter?.extra?.autoFilterMetaData?.subList?.some(
      (s) => s.includes(criteria),
    );

    return { isFoundInList, isFoundInSubList };
  };

  const getRootAutoFiltersNeedToBeFetched = (
    f: ITreeItem[],
    criteria: string,
  ) => {
    const autoFilterIds: string[] = [];

    const processAutoFilter = (filter: ITreeItem, criteria: string) => {
      const isFetched = !!filter?.children?.length;
      const noMetaData = !!filter?.extra?.autoFilterMetaData?.list?.length;

      if (isFetched || !noMetaData) {
        return;
      }

      const { isFoundInList, isFoundInSubList } = getIfCriteriaFoundOnMetaData(
        filter,
        criteria,
      );

      if (
        isFoundInList ||
        (isFoundInSubList &&
          filter.extra.filterType === FilterTypes.AUTO_FILTER_ROOT)
      ) {
        autoFilterIds.push(filter.id);
      }
    };

    const traverseFilters = (filters: any, criteria: string) => {
      for (const filter of filters) {
        processAutoFilter(filter, criteria);
        if (filter.children && filter.children.length > 0) {
          traverseFilters(filter.children, criteria);
        }
      }
    };

    traverseFilters(f, criteria);

    return autoFilterIds;
  };

  const hasDescendantsMatchingCriteria = (
    node: ITreeItem,
    lowercaseCriteria: string,
  ): boolean => {
    if (!node?.children?.length) {
      return false;
    }

    for (const item of node.children) {
      if (item.name.toLowerCase().includes(lowercaseCriteria)) {
        return true;
      }
      if (hasDescendantsMatchingCriteria(item, lowercaseCriteria)) {
        return true;
      }
    }
    return false;
  };

  const getSearchOpenNodes = (
    filteredTree: ITreeItem[],
    criteria: string,
  ): string[] => {
    const openNodes: string[] = [];
    const lowercaseCriteria = criteria.toLowerCase();

    const traverseTree = (node: ITreeItem) => {
      // Check if any descendant matches the criteria
      if (
        node.children &&
        hasDescendantsMatchingCriteria(node, lowercaseCriteria)
      ) {
        openNodes.push(`${node.id}`);
        for (const child of node.children) {
          traverseTree(child);
        }
      }
    };

    for (const node of filteredTree) {
      traverseTree(node);
    }

    return openNodes;
  };

  const performServerSearch = async (
    scope: IPanelScope,
    criteria: string,
  ): Promise<ISearchResult> => {
    // Abort any ongoing search request for the same project/module
    if (abortControllers.search) {
      abortControllers.search.abort();
    }

    // Create a new AbortController
    const controller = new AbortController();
    abortControllers["search"] = controller;

    try {
      const searchUrl =
        scope.tab === FOLDERS_TAB_KEY
          ? `/p/${scope.projectId}/${scope.tab}/search?folder_type=${scope.module.toLowerCase() === "test" ? "test" : "testset"}`
          : `/projects/${scope.projectId}/custom_views/search?entity_name=${scope.module}`;

      const searchRes = await HttpService.get(
        `${searchUrl}&query=${criteria.toLowerCase()}`,
        {},
        controller.signal, // Pass the signal to the request
      );

      const res =
        scope.tab === FOLDERS_TAB_KEY
          ? FoldersService.transformServerToClient(searchRes.items)
          : await FiltersService.transformServerToClient(searchRes.items);

      const matchingIds: string[] = SearchService.getSearchOpenNodes(
        res,
        criteria,
      );

      return {
        filteredTree: res,
        matchingIds: matchingIds,
        fullResults: searchRes.limited,
      };
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Request aborted:", criteria);
        return { filteredTree: [], matchingIds: [], fullResults: false }; // Return empty result if aborted
      } else {
        throw error; // Handle other errors
      }
    }
  };

  const serverSearch = (() => {
    let debounced: ReturnType<typeof debounce> | null = null;

    return (scope: IPanelScope, criteria: string): Promise<ISearchResult> => {
      if (!debounced) {
        debounced = debounce(
          (
            scope: IPanelScope,
            criteria: string,
            resolve: (res: ISearchResult) => void,
            reject: (err: any) => void,
          ) => {
            performServerSearch(scope, criteria).then(resolve).catch(reject);
          },
          300,
        );
      }

      return new Promise((resolve, reject) =>
        debounced!(scope, criteria, resolve, reject),
      );
    };
  })();

  return {
    search,
    serverSearch,
    getNumberOfResults,
    findFilterItem,
    searchNodeTree,
    getParentNodeIds,
    getParents,
    getRootAutoFiltersNeedToBeFetched,
    hasDescendantsMatchingCriteria,
    getSearchOpenNodes,
  };
};

export const SearchService = searchService();
